import React, { useEffect, useState, useContext } from "react";
import { Box, Typography } from "@mui/material";
import Divider from "@mui/material/Divider";
import { ThemeContext } from "../Homepage";
import { getGatewayConnectionAnalysis } from "../../Restclient/datacollection";

// KQL already shifts timestamps by +5:30 to produce IST wall-clock values.
// They arrive as UTC-labelled strings, so use UTC getters to avoid a second +5:30 conversion.
const formatISTFromKQL = (ts) => {
  if (!ts) return '-';
  const d = new Date(ts);
  if (isNaN(d.getTime())) return '-';
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getUTCDate())}/${pad(d.getUTCMonth() + 1)}/${d.getUTCFullYear()}  ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`;
};

export default function GatewayConnectionAnalysis() {
  const theme = useContext(ThemeContext);
  const [connectionAnalysis, setConnectionAnalysis] = useState([]);
  const [analysisLoading, setAnalysisLoading] = useState(false);

  useEffect(() => {
    setAnalysisLoading(true);
    getGatewayConnectionAnalysis((err, data) => {
      setAnalysisLoading(false);
      if (!err && data && data.data) {
        setConnectionAnalysis(data.data);
      } else {
        setConnectionAnalysis([]);
      }
    });
  }, []);

  return (
    <Box sx={{ height: '100%', width: '100%', boxSizing: 'border-box', padding: '8px' }}>
      <Typography style={{ fontSize: '20px', fontWeight: 400, color: '#008CD7', padding: '4px 0px', marginBottom: '8px' }}>
        Gateway Connection Analysis (Today - IST)
      </Typography>
      {analysisLoading ? (
        <Typography style={{ fontSize: '14px', color: 'var(--GreyBaseColor)', padding: '8px' }}>Loading analysis...</Typography>
      ) : (
        <Box sx={{ width: '100%', overflowX: 'auto', background: 'var(--SecondaryBackground)' }}>
          {/* Table Header */}
          <Box sx={{ display: 'flex', flexDirection: 'row', width: '100%', gap: '0px', padding: '8px 12px', boxSizing: 'border-box', background: theme === 'dark' ? '#3C3C3C40' : '#EFEFEF', minWidth: '900px' }}>
            <Typography style={{ fontSize: '14px', fontWeight: 700, color: theme === 'dark' ? '#C1C1C1' : '#3C3C3C', width: '18%' }}>Gateway ID</Typography>
            <Typography style={{ fontSize: '14px', fontWeight: 700, color: theme === 'dark' ? '#C1C1C1' : '#3C3C3C', width: '22%' }}>Connected At (IST)</Typography>
            <Typography style={{ fontSize: '14px', fontWeight: 700, color: theme === 'dark' ? '#C1C1C1' : '#3C3C3C', width: '28%' }}>Disconnected At (IST)</Typography>
            <Typography style={{ fontSize: '14px', fontWeight: 700, color: theme === 'dark' ? '#C1C1C1' : '#3C3C3C', width: '16%', textAlign: 'center' }}>Session Duration (min)</Typography>
            <Typography style={{ fontSize: '14px', fontWeight: 700, color: theme === 'dark' ? '#C1C1C1' : '#3C3C3C', width: '16%', textAlign: 'center' }}>Total Uptime Today (min)</Typography>
          </Box>
          <Divider style={{ margin: '0px', background: theme === 'dark' ? '#3C3C3C' : '#DEDEDE' }} />
          {/* Table Body */}
          <Box sx={{ maxHeight: 'calc(100vh - 220px)', overflowY: 'auto', minWidth: '900px', '::-webkit-scrollbar': { width: '4px', height: '4px' }, '::-webkit-scrollbar-track': { background: theme === 'dark' ? '#3c3c3c' : '#c1c1c1' }, '::-webkit-scrollbar-thumb': { background: '#7e7e7e', borderRadius: '5px' } }}>
            {connectionAnalysis.length === 0 ? (
              <Box sx={{ padding: '16px' }}>
                <Typography style={{ fontSize: '14px', color: 'var(--GreyBaseColor)' }}>No connection data available for today.</Typography>
              </Box>
            ) : (
              connectionAnalysis.map((row, idx) => (
                <React.Fragment key={idx}>
                  <Box sx={{ display: 'flex', flexDirection: 'row', width: '100%', gap: '0px', padding: '8px 12px', boxSizing: 'border-box', background: idx % 2 === 0 ? (theme === 'dark' ? '#1E1E1E' : '#FFFFFF') : (theme === 'dark' ? '#2A2A2A' : '#F9F9F9') }}>
                    <Typography style={{ fontSize: '13px', fontWeight: 400, color: theme === 'dark' ? '#C1C1C1' : '#3C3C3C', width: '18%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.GatewayId}</Typography>
                    <Typography style={{ fontSize: '13px', fontWeight: 400, color: theme === 'dark' ? '#C1C1C1' : '#3C3C3C', width: '22%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{formatISTFromKQL(row.ConnectedAt_IST)}</Typography>
                    <Typography style={{ fontSize: '13px', fontWeight: 400, color: row.DisconnectedAt_IST === 'Still Connected (Active)' ? '#00943d' : (theme === 'dark' ? '#C1C1C1' : '#3C3C3C'), width: '28%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.DisconnectedAt_IST === 'Still Connected (Active)' ? 'Still Connected (Active)' : formatISTFromKQL(row.DisconnectedAt_IST)}</Typography>
                    <Typography style={{ fontSize: '13px', fontWeight: 400, color: theme === 'dark' ? '#C1C1C1' : '#3C3C3C', width: '16%', textAlign: 'center' }}>{row.SessionDurationMinutes ?? '-'}</Typography>
                    <Typography style={{ fontSize: '13px', fontWeight: 400, color: theme === 'dark' ? '#C1C1C1' : '#3C3C3C', width: '16%', textAlign: 'center' }}>{row.TotalUptimeMinutesToday ?? '-'}</Typography>
                  </Box>
                  <Divider style={{ margin: '0px', background: theme === 'dark' ? '#3C3C3C' : '#EFEFEF' }} />
                </React.Fragment>
              ))
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
}
